/*
  THE COLOUR IN A COLOUR SWATCH

  A swatch must show the colour of the garment — a flat, solid colour, never
  a little photograph of the garment. Shopify can hold that colour on the
  option value itself, and where it does, Liquid has already painted it and
  this file leaves the square alone.

  Where it does not, the colour is taken from the shop's own photograph of
  that colour, in the shopper's browser, and painted flat. Nothing is
  invented and no colour is guessed from the name.

  How the photograph is read:

  Every photograph in this catalogue is a person standing in the middle of a
  paper sweep, so the middle of the frame is cloth and the border is sweep.
  The sweep is read off that border and struck out of the middle — not by its
  exact colour, because a studio light standing to one side makes it brighter
  at one edge than the other, but by its hue, so a shaded fold of sweep goes
  out with it. Whatever is left standing is on the model, and the middle of
  all of it tells us which column of the picture she occupies. If striking
  out the sweep leaves nothing, the garment is itself white, off-white or
  grey — a colour the sweep shares — and the middle of the frame is taken as
  it stands. The survivors are then voted on and the winning patch is
  averaged down to one hex.

  assets/../algo.py in the workshop is the same routine in Python, and the
  two are checked against each other on the same pictures.
*/
(function () {
  'use strict';
  if (window.__refSwatchColor) return;
  window.__refSwatchColor = true;

  var W = 56;              // the photo is read at thumbnail size
  var ATTR = 'data-swatch-src';
  var STORE = 'refSwatchColor';
  var done = new WeakSet ? new WeakSet() : null;
  var cache = {};          // url -> hex, for this page
  var saved = {};          // url -> hex, carried between pages

  try {
    saved = JSON.parse(sessionStorage.getItem(STORE) || '{}') || {};
  } catch (e) {
    saved = {};
  }

  function remember(url, hex) {
    saved[url] = hex;
    try {
      sessionStorage.setItem(STORE, JSON.stringify(saved));
    } catch (e) {
      /* a private window, or the box is full; the colour still shows */
    }
  }

  function median(a) {
    a.sort(function (x, y) { return x - y; });
    return a[a.length >> 1];
  }

  function hex2(n) {
    n = n < 0 ? 0 : n > 255 ? 255 : Math.round(n);
    return (n < 16 ? '0' : '') + n.toString(16);
  }

  function dominant(img) {
    var w = img.naturalWidth || img.width;
    var h = img.naturalHeight || img.height;
    if (!w || !h) return null;
    var sw = W;
    var sh = Math.max(1, Math.round(h * W / w));
    var cv = document.createElement('canvas');
    cv.width = sw;
    cv.height = sh;
    var ctx = cv.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, sw, sh);
    var data;
    try {
      data = ctx.getImageData(0, 0, sw, sh).data;
    } catch (e) {
      return null;     // the photo came from somewhere that forbids reading it
    }
    var at = function (x, y) {
      var i = (y * sw + x) * 4;
      return [data[i], data[i + 1], data[i + 2]];
    };

    /* 1 — the sweep, read off the border of the photograph. */
    var m = Math.max(1, sw >> 3);
    var fr = [[], [], []], x, y, c, i;
    for (y = 0; y < sh; y++) {
      for (x = 0; x < m; x++) {
        c = at(x, y);
        fr[0].push(c[0]); fr[1].push(c[1]); fr[2].push(c[2]);
        c = at(sw - 1 - x, y);
        fr[0].push(c[0]); fr[1].push(c[1]); fr[2].push(c[2]);
      }
    }
    var bg = [median(fr[0]), median(fr[1]), median(fr[2])];
    var bgL = (bg[0] + bg[1] + bg[2]) / 3;

    /* Sweep, and every shaded fold of it: the same hue as the border,
       whatever its brightness. Anything markedly darker than the border is
       cloth in its own right and is left alone. */
    function sweepish(p) {
      var l = (p[0] + p[1] + p[2]) / 3;
      if (bgL < 1 || l < bgL * 0.55) return false;
      var k = bgL / l, d = 0, j, v;
      for (j = 0; j < 3; j++) {
        v = p[j] * k;
        if (v > 255) v = 255;
        d += (v - bg[j]) * (v - bg[j]);
      }
      return d <= 400;
    }

    /* 2 — the column of the picture the model stands in. */
    var y0 = Math.floor(sh * 0.30), y1 = Math.floor(sh * 0.68);
    if (y1 <= y0) { y0 = 0; y1 = sh; }
    var marks = [];
    for (y = y0; y < y1; y++) {
      for (x = 0; x < sw; x++) {
        if (!sweepish(at(x, y))) marks.push(x);
      }
    }
    var half = Math.max(1, Math.floor(sw * 0.13));
    var cx = marks.length >= (y1 - y0) * sw * 0.03 ? median(marks) : sw >> 1;
    var x0 = Math.max(0, cx - half), x1 = Math.min(sw, cx + half + 1);
    if (x1 <= x0) { x0 = 0; x1 = sw; }

    var middle = [], cloth = [], p;
    for (y = y0; y < y1; y++) {
      for (x = x0; x < x1; x++) {
        p = at(x, y);
        middle.push(p);
        if (!sweepish(p)) cloth.push(p);
      }
    }

    /* If the sweep took nearly everything with it, the garment is white,
       off-white or grey. Take the middle of the frame as it stands. */
    var pool = cloth.length >= Math.max(24, middle.length * 0.45) ? cloth : middle;
    if (!pool.length) return null;

    /* 3 — the most crowded corner of colour space, then the true average of
       the pixels standing in it, so the answer is a colour off the cloth
       rather than the centre of a bucket. */
    var bins = {}, key, best = null, bestN = -1;
    for (i = 0; i < pool.length; i++) {
      p = pool[i];
      key = (p[0] >> 4) + ',' + (p[1] >> 4) + ',' + (p[2] >> 4);
      (bins[key] || (bins[key] = [])).push(p);
    }
    for (key in bins) {
      if (bins[key].length > bestN) { bestN = bins[key].length; best = bins[key]; }
    }
    var r = 0, g = 0, b = 0;
    for (i = 0; i < best.length; i++) {
      r += best[i][0]; g += best[i][1]; b += best[i][2];
    }
    var n = best.length;
    return '#' + hex2(r / n) + hex2(g / n) + hex2(b / n);
  }

  function paint(el, hex) {
    el.style.backgroundColor = hex;
    el.style.backgroundImage = 'none';
  }

  function resolve(el) {
    if (done && done.has(el)) return;
    if (done) done.add(el);
    var url = el.getAttribute(ATTR);
    if (!url) return;

    var known = cache[url] || saved[url];
    if (known) { paint(el, known); return; }

    var waiting = cache['@' + url];
    if (waiting) { waiting.push(el); return; }
    cache['@' + url] = [el];

    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = function () {
      var hex = null;
      try {
        hex = dominant(img);
      } catch (e) {
        hex = null;
      }
      var queue = cache['@' + url] || [];
      delete cache['@' + url];
      if (!hex) return;        // the square keeps the colour Liquid painted
      cache[url] = hex;
      remember(url, hex);
      for (var i = 0; i < queue.length; i++) paint(queue[i], hex);
    };
    img.onerror = function () { delete cache['@' + url]; };
    img.src = url;
  }

  function sweep(root) {
    var list = (root || document).querySelectorAll('[' + ATTR + ']');
    for (var i = 0; i < list.length; i++) resolve(list[i]);
  }

  function start() {
    sweep(document);
    // Product grids bring more cards in as the shopper scrolls, and the
    // theme editor replaces whole sections; both arrive as new nodes.
    if (window.MutationObserver) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.hasAttribute && n.hasAttribute(ATTR)) resolve(n);
            if (n.querySelectorAll) sweep(n);
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}());
